/**
 * Step 4's recipient table — one row per copy, one cell per role the batch did
 * not pin. It stays mounted while the rows are checked, so a flagged row is
 * fixed where it was typed.
 *
 * The inline marks read the same `checkTypedRows` pass the panel below reads:
 * a cell marked red here is exactly the cell the worklist names, and nothing
 * can disagree about how many rows are ready.
 */

import React from "react";
import { Box } from "@mui/material";
import CustomContactNumberInput from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import strings from "global/constants/StringConstants";
import { typedRowsStyles } from "../BulkSend.styles";
import tokens from "../BulkSend.tokens";
import {
  BulkCellField,
  BulkRowCheckSummary,
  BulkSignerSlot,
  BULK_ACCESS_CODE_MAX,
  BULK_MAX_ROWS,
  needsPhoneFor,
  phoneReasonFor,
} from "../BulkSendTypesAndValidation";
import { isDialCodeOnly } from "../BulkSendHelper";
import { CloseIcon, PlusIcon } from "./BulkSendIcons";

const t = tokens.typedRows;
const s = typedRowsStyles;

/** One cell's worth of state: what the sender typed for a role on a row. */
export interface TypedCell {
  name: string;
  email: string;
  phone: string;
  /** The access code this recipient will be asked for, when the role uses one. */
  code: string;
}

export type TypedRow = Record<number, TypedCell>;

export const emptyCell = (): TypedCell => ({
  name: "",
  email: "",
  phone: "",
  code: "",
});

export const emptyRow = (slots: BulkSignerSlot[]): TypedRow =>
  slots.reduce((row: TypedRow, slot) => {
    row[slot.signerIndex] = emptyCell();
    return row;
  }, {});

/**
 * A row the sender has not touched is ignored rather than flagged. The phone
 * is tested with `isDialCodeOnly` because the contact control seeds itself
 * with a dial code, so an untouched cell is never literally empty.
 */
export const isRowEmpty = (row: TypedRow): boolean =>
  Object.values(row).every(
    (cell) =>
      !cell.name.trim() &&
      !cell.email.trim() &&
      isDialCodeOnly(cell.phone) &&
      !(cell.code || "").trim(),
  );

/**
 * A role reached by text needs a phone from the list, which is why the column
 * appears at all — the Signers step decides it, this table asks for it.
 */
const textsRole = (slot: BulkSignerSlot): boolean =>
  needsPhoneFor(slot.channels ?? ["EMAIL"]) || slot.security === "sms";

/**
 * A role secured by an access code needs one code *per recipient* — sharing a
 * single code across the batch would let any recipient open every copy — so
 * the code is a column of the list, exactly like the phone.
 */
const codesRole = (slot: BulkSignerSlot): boolean => slot.security === "code";

/** What "Fix above" focuses. */
export const cellInputId = (
  rowIndex: number,
  signerIndex: number,
  field: BulkCellField,
) => `bulk-row-${rowIndex}-${signerIndex}-${field}`;

interface Props {
  slots: BulkSignerSlot[];
  rows: TypedRow[];
  /** The one derivation this table and the check panel below both read. */
  summary: BulkRowCheckSummary;
  onChange: (rowIndex: number, signerIndex: number, cell: TypedCell) => void;
  onAdd: () => void;
  onRemove: (rowIndex: number) => void;
}

const TypedRowsGrid: React.FC<Props> = ({
  slots,
  rows,
  summary,
  onChange,
  onAdd,
  onRemove,
}) => {
  const columns = `${t.numberWidth}px ${slots
    .map(
      (slot) =>
        `1fr 1.2fr${textsRole(slot) ? " 1.4fr" : ""}${
          codesRole(slot) ? " 0.8fr" : ""
        }`,
    )
    .join(" ")} ${t.removeSize}px`;

  return (
    <Box sx={s.scroller}>
      <Box sx={{ ...s.grid(columns), ...s.header }}>
        <Box component="span" />
        {slots.map((slot) => (
          <React.Fragment key={slot.signerIndex}>
            <Box component="span">
              {slot.label} name
              <Box component="span" sx={s.required}>
                *
              </Box>
            </Box>
            <Box component="span">
              {slot.label} email
              <Box component="span" sx={s.required}>
                *
              </Box>
            </Box>
            {textsRole(slot) && (
              <Box component="span">
                Phone
                <Box component="span" sx={s.required}>
                  *
                </Box>
                <Box component="span" sx={s.requiredNote}>
                  Required for{" "}
                  {phoneReasonFor(
                    slot.channels ?? ["EMAIL"],
                    slot.security ?? "none",
                  )}
                </Box>
              </Box>
            )}
            {codesRole(slot) && (
              <Box component="span">
                Access code
                <Box component="span" sx={s.required}>
                  *
                </Box>
                <Box component="span" sx={s.requiredNote}>
                  One per recipient — share it yourself
                </Box>
              </Box>
            )}
          </React.Fragment>
        ))}
        <Box component="span" />
      </Box>

      <Box sx={s.rowsArea}>
        {rows.map((row, rowIndex) => {
          const check = summary.checks[rowIndex];
          const isSkipped = check?.verdict === "skipped";

          return (
            <Box key={rowIndex} sx={s.row(isSkipped)}>
              <Box sx={{ ...s.grid(columns), px: 0 }}>
                <Box component="span" sx={s.rowNumber}>
                  {rowIndex + 1}
                </Box>

                {slots.map((slot) => {
                  const cell = row[slot.signerIndex] ?? emptyCell();
                  /* Every cell the check named is marked, not just the first. */
                  const issueFor = (field: BulkCellField) =>
                    check?.verdict === "invalid" ||
                    check?.verdict === "incomplete"
                      ? check.issues.find(
                          (issue) =>
                            issue.signerIndex === slot.signerIndex &&
                            issue.field === field,
                        )
                      : undefined;
                  const nameIssue = issueFor("name");
                  const emailIssue = issueFor("email");
                  const phoneIssue = issueFor("phone");
                  const codeIssue = issueFor("code");

                  return (
                    <React.Fragment key={slot.signerIndex}>
                      <Box sx={s.cell}>
                        <Box
                          component="input"
                          id={cellInputId(rowIndex, slot.signerIndex, "name")}
                          value={cell.name}
                          placeholder="Name"
                          aria-label={`${slot.label} name, row ${rowIndex + 1}`}
                          aria-invalid={Boolean(nameIssue)}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) =>
                            onChange(rowIndex, slot.signerIndex, {
                              ...cell,
                              name: event.target.value,
                            })
                          }
                          sx={s.input(Boolean(nameIssue))}
                        />
                        {nameIssue && (
                          <Box component="span" sx={s.inlineMessage}>
                            {nameIssue.message}
                          </Box>
                        )}
                      </Box>

                      <Box sx={s.cell}>
                        <Box
                          component="input"
                          type="email"
                          id={cellInputId(rowIndex, slot.signerIndex, "email")}
                          value={cell.email}
                          placeholder="Email"
                          aria-label={`${slot.label} email, row ${rowIndex + 1}`}
                          aria-invalid={Boolean(emailIssue)}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) =>
                            onChange(rowIndex, slot.signerIndex, {
                              ...cell,
                              email: event.target.value,
                            })
                          }
                          sx={s.input(Boolean(emailIssue))}
                        />
                        {emailIssue && (
                          <Box component="span" sx={s.inlineMessage}>
                            {emailIssue.message}
                          </Box>
                        )}
                      </Box>

                      {textsRole(slot) && (
                        <Box sx={s.cell}>
                          <Box sx={s.phoneCell}>
                            <CustomContactNumberInput
                              id={cellInputId(
                                rowIndex,
                                slot.signerIndex,
                                "phone",
                              )}
                              placeHolder={strings.smsDelivery.phonePlaceholder}
                              value={cell.phone}
                              countries={strings.SMS_SUPPORTED_COUNTRIES}
                              error={phoneIssue ? " " : undefined}
                              inputProps={{
                                "aria-label": `${slot.label} phone, row ${
                                  rowIndex + 1
                                }`,
                              }}
                              onChange={(value: string) =>
                                onChange(rowIndex, slot.signerIndex, {
                                  ...cell,
                                  phone: value,
                                })
                              }
                            />
                          </Box>
                          {phoneIssue && (
                            <Box component="span" sx={s.inlineMessage}>
                              {phoneIssue.message}
                            </Box>
                          )}
                        </Box>
                      )}

                      {codesRole(slot) && (
                        <Box sx={s.cell}>
                          <Box
                            component="input"
                            id={cellInputId(rowIndex, slot.signerIndex, "code")}
                            value={cell.code}
                            placeholder="e.g. 4821"
                            maxLength={BULK_ACCESS_CODE_MAX}
                            autoComplete="off"
                            aria-label={`${slot.label} access code, row ${rowIndex + 1}`}
                            aria-invalid={Boolean(codeIssue)}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              onChange(rowIndex, slot.signerIndex, {
                                ...cell,
                                code: event.target.value,
                              })
                            }
                            sx={s.input(Boolean(codeIssue))}
                          />
                          {codeIssue && (
                            <Box component="span" sx={s.inlineMessage}>
                              {codeIssue.message}
                            </Box>
                          )}
                        </Box>
                      )}
                    </React.Fragment>
                  );
                })}

                <Box
                  component="button"
                  type="button"
                  aria-label={`Remove row ${rowIndex + 1}`}
                  disabled={rows.length === 1}
                  onClick={() => onRemove(rowIndex)}
                  sx={s.remove}
                >
                  <CloseIcon size={t.removeIconSize} />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={s.addRow}>
        <Box
          component="button"
          type="button"
          onClick={onAdd}
          disabled={rows.length >= BULK_MAX_ROWS}
          sx={s.add}
        >
          <PlusIcon size={t.addIconSize} />
          Add a recipient
        </Box>
        <Box component="span" sx={s.pasteHint}>
          Paste a column from a spreadsheet to fill several rows at once.
        </Box>
      </Box>
    </Box>
  );
};

export default TypedRowsGrid;
